import { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMedkit, FaCreditCard, FaHandHoldingHeart, FaShieldAlt, FaIdCard, FaUsers } from 'react-icons/fa';
import useApi from '../hooks/useApi';
import { useSeason } from '../contexts/SeasonContext';
import PageHeader from '../components/PageHeader';
import TabNav from '../components/TabNav';
import ScrollReveal, { StaggerReveal, StaggerItem } from '../components/ScrollReveal';
import { FIXED_H } from '../components/layoutConstants';
import { TYPE_META, groupByJour } from '../components/PlanningCreneaux';
import { trackInscriptionClick } from '../utils/analytics';

const tabDefs = [
  { key: 'tarifs',   label: 'Tarifs',          eyebrow: 'Nos formules',        title: 'Nos Tarifs'                     },
  { key: 'adhesion', label: 'Adhésion',        eyebrow: 'Bon à savoir',        title: 'Ce que comprend votre adhésion' },
  { key: 'infos',    label: 'Infos pratiques', eyebrow: "Avant de s'inscrire", title: 'Infos pratiques'                },
];

const CategorieCard = ({ cat }) => {
  const jours = groupByJour(cat.creneaux || []);
  return (
  <div className="card-minimal p-6 flex flex-col h-full">
    <h3 className="font-bold text-stone-950 text-2xl">{cat.nom}</h3>
    {cat.description && <p className="text-sm text-stone-500 mt-0.5">{cat.description}</p>}
    {cat.tarif && <p className="text-3xl font-extrabold text-brand-800 mt-4">{cat.tarif}</p>}

    {jours.length > 0 && (
      <div className="mt-5 pt-5 border-t border-stone-100 space-y-3.5 flex-1">
        <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide">Accès inclus</p>
        {jours.map(({ jour, items }) => (
          <div key={jour}>
            <p className="text-sm font-bold text-stone-800">{jour}</p>
            <div className="mt-1 space-y-1.5">
              {items.map(c => {
                const activites = (c.activites || []).filter(a => TYPE_META[a.type_creneau]);
                if (!activites.length) return null;
                return (
                  <div key={c.creneau_id} className="flex items-start gap-2 text-sm text-stone-600">
                    <span className="shrink-0 tabular-nums text-stone-500 w-[4.5rem]">{c.horaire}</span>
                    <div className="flex-1 space-y-0.5">
                      {activites.map((a, i) => {
                        const meta = TYPE_META[a.type_creneau];
                        return (
                          <p key={i} className="flex items-center gap-1.5">
                            <span className="shrink-0">{meta.icon}</span>
                            <span>{meta.label}{a.specificite ? ` ${a.specificite}` : ''}</span>
                          </p>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    )}

    <div className="mt-6">
      <a
        href={cat.url_inscription || 'https://adherer.ffbad.club/bce95'}
        target="_blank" rel="noopener noreferrer"
        onClick={trackInscriptionClick}
        className="btn-brand w-full text-center text-sm"
      >
        S'inscrire
      </a>
    </div>
  </div>
  );
};

const GROUPE_LABELS = { adulte: 'Adultes', jeunes: 'Jeunes' };
const GROUPE_ORDER  = ['adulte', 'jeunes'];

const InfoItem = ({ icon: Icon, title, children }) => (
  <StaggerItem className="flex gap-4">
    <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-800 flex items-center justify-center shrink-0">
      <Icon size={17} />
    </div>
    <div>
      <h3 className="font-semibold text-stone-950 mb-1 text-base">{title}</h3>
      <p className="text-sm text-stone-500 leading-relaxed">{children}</p>
    </div>
  </StaggerItem>
);

const Tarifs = ({ setCurrentSection, currentSection }) => {
  const { data: categories, loading } = useApi('categories');
  const { seasonLabel } = useSeason();

  const { tab }     = useParams();
  const navigate    = useNavigate();
  const stickyRef   = useRef(null);
  const sectionRefs = useRef({});
  const activeRef   = useRef(null);
  const isProg      = useRef(false);
  const [active, setActive] = useState(tab || 'tarifs');

  const scrollOffset = useCallback(() => FIXED_H + (stickyRef.current?.offsetHeight ?? 0), []);

  const goTo = useCallback((key, behavior = 'smooth') => {
    const el = sectionRefs.current[key];
    if (!el) return;
    isProg.current = true;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - scrollOffset(), behavior });
    clearTimeout(goTo._t);
    goTo._t = setTimeout(() => { isProg.current = false; }, 900);
    setActive(key);
    navigate(`/tarifs/${key}`, { replace: true });
  }, [navigate, scrollOffset]);

  useEffect(() => {
    const key = tab || 'tarifs';
    if (key !== 'tarifs') { const t = setTimeout(() => goTo(key, 'instant'), 120); return () => clearTimeout(t); }
  }, []); // eslint-disable-line

  useEffect(() => {
    const onScroll = () => {
      if (isProg.current) return;
      const threshold = scrollOffset() + 40;
      let current = tabDefs[0].key;
      for (const { key } of tabDefs) {
        const el = sectionRefs.current[key];
        if (el && el.getBoundingClientRect().top <= threshold) current = key;
      }
      if (current !== activeRef.current) { activeRef.current = current; setActive(current); navigate(`/tarifs/${current}`, { replace: true }); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [navigate, scrollOffset]);

  const ref = (key) => (el) => { sectionRefs.current[key] = el; };
  const meta = tabDefs.find(t => t.key === active);

  const groupedCategories = GROUPE_ORDER
    .map(groupe => ({ groupe, items: categories.filter(c => c.groupe === groupe) }))
    .filter(g => g.items.length > 0);

  return (
    <>
      <PageHeader
        title="Tarifs"
        subtitle={`Nos formules d'adhésion pour la saison ${seasonLabel || ''}`.trim()}
        setCurrentSection={setCurrentSection}
        currentSection={currentSection}
      />

      <div ref={stickyRef} className="sticky z-30 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-[0_1px_0_rgba(0,0,0,0.04)]" style={{ top: FIXED_H }}>
        <div className="max-w-6xl mx-auto px-5 md:pl-8 py-3 flex items-center gap-3 min-h-[52px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }} className="flex items-center gap-3">
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-brand-800/60">{meta?.eyebrow}</span>
              <div className="w-px h-4 bg-stone-200 shrink-0" />
              <span className="text-sm font-bold text-stone-950">{meta?.title}</span>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="border-t border-stone-100">
          <div className="max-w-6xl mx-auto px-5 md:pl-8">
            <TabNav tabs={tabDefs} activeTab={active} onTabChange={goTo} />
          </div>
        </div>
      </div>

      {/* Tarifs */}
      <div ref={ref('tarifs')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto space-y-14">
            {!loading && categories.length === 0 ? (
              <p className="text-stone-400 text-sm text-center py-8">Les tarifs de la saison seront bientôt disponibles.</p>
            ) : (
              groupedCategories.map(({ groupe, items }) => (
                <ScrollReveal key={groupe}>
                  <h2 className="display-md mb-6">{GROUPE_LABELS[groupe] || groupe}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                    {items.map(cat => <CategorieCard key={cat.id} cat={cat} />)}
                  </div>
                </ScrollReveal>
              ))
            )}
          </div>
        </section>
      </div>

      {/* Ce que comprend l'adhésion */}
      <div ref={ref('adhesion')}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-10">
              <span className="eyebrow mb-3 block">Bon à savoir</span>
              <h2 className="display-md">Ce que comprend votre adhésion</h2>
            </ScrollReveal>
            <StaggerReveal className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
              <InfoItem icon={FaIdCard} title="Licence FFBaD">
                Votre adhésion inclut la licence de la Fédération Française de Badminton, valable sur toute la saison.
              </InfoItem>
              <InfoItem icon={FaShieldAlt} title="Assurance">
                Une assurance individuelle accident est comprise, couvrant la pratique du badminton en club et en compétition.
              </InfoItem>
              <InfoItem icon={FaUsers} title="Encadrement diplômé">
                Les entraînements sont encadrés par des éducateurs diplômés, pour tous les niveaux du débutant au confirmé.
              </InfoItem>
              <InfoItem icon={FaHandHoldingHeart} title="Vie du club">
                Accès aux animations, tournois internes et événements organisés tout au long de la saison.
              </InfoItem>
            </StaggerReveal>
          </div>
        </section>
      </div>

      {/* Infos pratiques */}
      <div ref={ref('infos')}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-10">
              <span className="eyebrow mb-3 block">Avant de s'inscrire</span>
              <h2 className="display-md">Infos pratiques</h2>
            </ScrollReveal>
            <StaggerReveal className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8 mb-10">
              <InfoItem icon={FaMedkit} title="Certificat médical">
                Un questionnaire de santé suffit si vous répondez "non" à toutes les questions — sinon un certificat médical de non contre-indication est demandé.
                {' '}<Link to="/inscription/modalite" onClick={() => setCurrentSection('inscription')} className="text-brand-800 font-medium hover:underline">Voir les documents</Link>.
              </InfoItem>
              <InfoItem icon={FaCreditCard} title="Modes de paiement">
                Paiement par carte bancaire en ligne (jusqu'à 3 fois), ou tout autre moyen (chèque, espèces, Coupon Sport ANCV…) via un dossier papier.
                {' '}<Link to="/inscription/modalite" onClick={() => setCurrentSection('inscription')} className="text-brand-800 font-medium hover:underline">En savoir plus</Link>.
              </InfoItem>
            </StaggerReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              <div className="card-minimal p-5">
                <p className="font-semibold text-stone-900 text-sm mb-1">Pass'Sport</p>
                <p className="text-sm text-stone-500">Allocation de rentrée sportive déductible de votre adhésion pour les jeunes éligibles.{' '}
                  <Link to="/inscription/passSport" onClick={() => setCurrentSection('inscription')} className="text-brand-800 font-medium hover:underline">Vérifier mon éligibilité</Link>
                </p>
              </div>
              <div className="card-minimal p-5">
                <p className="font-semibold text-stone-900 text-sm mb-1">Coupon Sport ANCV</p>
                <p className="text-sm text-stone-500">Accepté comme moyen de paiement, à joindre à un dossier d'inscription papier.{' '}
                  <Link to="/inscription/couponSport" onClick={() => setCurrentSection('inscription')} className="text-brand-800 font-medium hover:underline">Voir les modalités</Link>
                </p>
              </div>
            </div>
            <div className="space-y-3 max-w-3xl">
              <div className="flex gap-3 p-5 rounded-2xl bg-amber-50 border border-amber-100">
                <p className="text-sm text-amber-800 leading-relaxed">
                  <span className="font-semibold">Équipement non fourni</span> — les volants ne sont pas fournis par le club, chaque joueur doit apporter les siens. Une raquette est nécessaire dès la première séance.
                </p>
              </div>
              <div className="flex gap-3 p-5 rounded-2xl bg-stone-100 border border-stone-200">
                <p className="text-sm text-stone-600 leading-relaxed">
                  <span className="font-semibold text-stone-800">Politique de remboursement</span> — aucun remboursement n'est possible après validation de l'inscription, quel qu'en soit le motif.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* CTA final */}
      <section className="relative overflow-hidden" style={{ background: 'var(--gradient-brand)' }}>
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #fff 0px, #fff 1px, transparent 1px, transparent 40px), repeating-linear-gradient(-45deg, #fff 0px, #fff 1px, transparent 1px, transparent 40px)',
        }} />
        <ScrollReveal className="relative z-10 max-w-6xl mx-auto px-5 py-20 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tighter mb-4">Prêt à nous rejoindre ?</h2>
          <p className="text-white/70 text-lg mb-10 max-w-lg mx-auto">
            Choisissez votre formule et inscrivez-vous en ligne en quelques minutes.
          </p>
          <Link to="/inscription" onClick={() => { setCurrentSection('inscription'); trackInscriptionClick(); }}
            className="btn bg-white text-brand-800 px-8 py-3.5 font-semibold hover:bg-white/90 shadow-xl">
            S'inscrire maintenant
          </Link>
        </ScrollReveal>
      </section>
    </>
  );
};

export default Tarifs;
